import { Link } from 'react-router-dom';
import { 
  Shield, AlertTriangle, Calendar, FileCheck, Clock,
  CheckCircle, ArrowRight, Phone, ChevronRight, Lock, Truck
} from 'lucide-react';
import SEO from '../components/shared/SEO';

// Compliance Plan Card
const PlanCard = ({ tier, price, period, features, highlight, color }) => (
  <div 
    className={`relative bg-[#121212] border ${highlight ? 'border-[#D4AF37]' : 'border-white/10'} p-8 flex flex-col`}
    data-testid={`protect-plan-${tier.toLowerCase()}`}
  >
    {highlight && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-wider">
        Most Popular
      </div>
    )}
    
    <div className="mb-6">
      <span 
        className="text-xs font-bold uppercase tracking-widest"
        style={{ color }}
      >
        {tier}
      </span>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="text-4xl font-bold text-white">${price}</span>
        <span className="text-white/50">/{period}</span>
      </div>
    </div>
    
    <ul className="space-y-3 flex-1 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-3 text-white/70 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color }} />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
    
    <Link
      to="/contact"
      className={`w-full py-4 text-center font-bold uppercase tracking-wider transition-colors ${
        highlight 
          ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]' 
          : 'border border-white/20 text-white hover:bg-white/5'
      }`}
    >
      Schedule Strategy Call
    </Link>
  </div>
);

// Feature Card
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-[#121212] border border-white/5 p-6">
    <div className="w-12 h-12 bg-[#C0C0C0]/10 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-[#C0C0C0]" />
    </div>
    <h4 className="text-white font-bold text-lg mb-2">{title}</h4>
    <p className="text-white/50 text-sm leading-relaxed">{description}</p>
  </div>
);

const ProtectPage = () => {
  const plans = [
    {
      tier: 'Basic',
      price: '197',
      period: 'month',
      color: '#888',
      features: [
        'Insurance expiration monitoring',
        'Annual UCR filing reminders',
        'Monthly compliance status report',
        'Email alerts for critical deadlines',
        'Basic regulatory guidance',
      ],
    },
    {
      tier: 'Professional',
      price: '297',
      period: 'month',
      color: '#C0C0C0',
      highlight: true,
      features: [
        'Everything in Basic, plus:',
        'IFTA & IRP deadline tracking',
        'Proactive compliance calendar',
        'Bi-weekly status updates',
        'Priority support access',
        'Safety score monitoring',
        'Audit preparation assistance',
      ],
    },
    {
      tier: 'Enterprise',
      price: '397',
      period: 'month',
      color: '#D4AF37',
      features: [
        'Everything in Professional, plus:',
        'Dedicated compliance manager',
        'Weekly compliance calls',
        'Real-time regulatory alerts',
        'Custom compliance dashboard',
        'Direct regulatory liaison',
      ],
    },
  ];

  const monitoringFeatures = [
    {
      icon: Calendar,
      title: 'Insurance Expirations',
      description: 'Track all insurance certificates and receive alerts before lapses that could shut down your authority.',
    },
    {
      icon: FileCheck,
      title: 'IFTA & IRP Deadlines',
      description: 'Never miss a quarterly fuel tax filing or registration renewal deadline again.',
    },
    {
      icon: AlertTriangle,
      title: 'UCR Filings',
      description: 'Annual Unified Carrier Registration monitoring to maintain your operating authority.',
    },
    {
      icon: Shield,
      title: 'Regulatory Risk Indicators',
      description: 'Early warning system for potential compliance issues before they become violations.',
    },
    {
      icon: Lock,
      title: 'Safety Status Visibility',
      description: 'Monitor your CSA scores and safety rating to stay in good standing with FMCSA.',
    },
    {
      icon: Clock,
      title: 'Proactive Reminders',
      description: 'Automated reminders ensure you never miss critical compliance milestones.',
    },
  ];

  return (
    <div data-testid="protect-page" className="bg-[#0A0A0A]">
      <SEO 
        title="PROTECT - Compliance Management | Queen B Logistics"
        description="Ongoing compliance oversight that prevents costly disruptions. Insurance monitoring, IFTA tracking, UCR filings, and regulatory risk management."
        canonical="https://queenblogistics.com/protect"
        keywords="trucking compliance, MC compliance, IFTA filing, IRP renewal, UCR registration, insurance monitoring, freight compliance"
      />

      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-black to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-white/50 hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4 text-white/30" />
              <span className="text-[#C0C0C0]">Protect</span>
            </div>

            {/* Title Block */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#C0C0C0]/10 flex items-center justify-center border border-[#C0C0C0]/30">
                <Shield className="w-8 h-8 text-[#C0C0C0]" />
              </div>
              <div>
                <span className="text-[#C0C0C0] text-sm font-bold uppercase tracking-widest">
                  Protect Your Business
                </span>
                <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
                  Compliance Management
                </h1>
              </div>
            </div>

            <p className="text-xl md:text-2xl text-white/80 font-medium mb-6">
              Ongoing oversight that prevents costly disruptions.
            </p>
            
            <p className="text-white/50 text-lg leading-relaxed max-w-2xl mb-10">
              Regulatory compliance is not optional — it's operational survival. 
              Our monitoring system ensures your authority remains protected through 
              deadline tracking, risk visibility, and structured oversight.
            </p>

            <Link
              to="/intake"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C0C0C0] text-black font-bold uppercase tracking-wider hover:bg-[#C0C0C0]/90 transition-colors"
              data-testid="protect-cta"
            >
              Select Compliance Plan
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* What We Monitor Section */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#C0C0C0] text-sm font-bold uppercase tracking-widest">
              Comprehensive Coverage
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              What We Monitor
            </h2>
            <div className="w-24 h-1 bg-[#C0C0C0] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {monitoringFeatures.map((feature, i) => (
              <FeatureCard key={i} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-16 bg-red-900/20 border-y border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-16 h-16 bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <h3 className="text-red-400 font-bold text-xl mb-2">
                Missing Compliance = Shutdown Risk
              </h3>
              <p className="text-white/70 leading-relaxed">
                One missed insurance renewal, one late IFTA filing, one overlooked UCR registration — 
                any of these can trigger authority suspension, fines, or complete operational shutdown. 
                Our structured oversight system ensures nothing falls through the cracks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="plans" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#C0C0C0] text-sm font-bold uppercase tracking-widest">
              Pricing Plans
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              Choose Your Protection Level
            </h2>
            <div className="w-24 h-1 bg-[#C0C0C0] mx-auto mb-6" />
            <p className="text-white/50 max-w-2xl mx-auto">
              Monthly compliance monitoring with no long-term contracts. 
              Cancel anytime with 30 days notice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <PlanCard key={plan.tier} {...plan} />
            ))}
          </div>

          {/* Optional Add-Ons */}
          <div className="mt-16 max-w-3xl mx-auto" data-testid="protect-addons">
            <div className="text-center mb-6">
              <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-[0.35em]">
                Optional Add-Ons
              </span>
              <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-white uppercase mt-2">
                Expand Your Coverage
              </h3>
            </div>
            <div className="bg-gradient-to-b from-[#141414] to-[#0a0a0a] border border-[#D4AF37]/30 p-6 md:p-8" data-testid="fleet-expansion-addon">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-6 h-6 text-[#D4AF37]" />
                    <h4 className="text-white font-bold text-xl">Fleet Expansion</h4>
                  </div>
                  <p className="text-white/60 text-sm">
                    Adds full compliance coverage for each additional power unit — stack it with any plan above.
                  </p>
                </div>
                <div className="text-left md:text-right whitespace-nowrap">
                  <div className="text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    +$75<span className="text-lg text-white/60">/mo</span>
                  </div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">per additional truck</div>
                </div>
              </div>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6">
                {[
                  'Compliance monitoring',
                  'Deadline tracking',
                  'Insurance monitoring',
                  'Registration monitoring',
                  'Reporting for each additional truck',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-white/80 text-sm">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-[#D4AF37] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/contact?addon=fleet-expansion"
                  className="flex-1 text-center bg-[#D4AF37] text-black px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5D47B] transition-colors"
                  data-testid="fleet-expansion-add-btn"
                >
                  Add to My Plan
                </Link>
                <Link
                  to="/contact"
                  className="flex-1 text-center border border-[#D4AF37] text-[#D4AF37] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="fleet-expansion-ask-btn"
                >
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>

          {/* Bundle Notice */}
          <div className="mt-12 max-w-3xl mx-auto">
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 text-center">
              <h4 className="text-[#D4AF37] font-bold text-lg mb-2">
                Bundle & Save 20%
              </h4>
              <p className="text-white/70 mb-4">
                Combine Compliance + Dispatch services and save 20% on your total monthly cost. 
                Get full lifecycle coverage for less.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F5D47B] transition-colors font-medium"
              >
                Learn About Bundle Pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Questions About <span className="text-[#C0C0C0]">Compliance?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Schedule a strategy call to discuss your compliance needs and find the right plan for your operation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
            data-testid="protect-final-cta"
          >
            <Phone className="w-5 h-5" />
            Schedule Strategy Call
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-white/30 text-xs text-center">
            Queen B Logistics provides compliance monitoring and reminder services. 
            Clients remain responsible for all government filings and regulatory submissions. 
            We do not file documents on your behalf.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ProtectPage;
